import * as React from 'react';
import { NavRunner } from './nav-runner';
import { FolderDialogProvider, NavWebchat } from './nav-webchat';
import { NavUser, ReferralDialogProvider } from './nav-user';
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarRail,
	useSidebar,
} from '@/components/ui/sidebar';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AccountSwitcher } from './account-switcher';
import { LocationContextIndicator } from './LocationContextIndicator';
import { ReactivateAccountButton } from './ReactivateAccountButton';
import { AiEmployeeWidget } from './AiEmployeeWidget';
import { FeatureNavigator, FeatureNavigatorStateProvider } from './FeatureNavigator';
import { PinnedFeatures } from './PinnedFeatures';
import { usePipelineSummaryPoller } from '@/hooks/usePipelineSummary';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import { canExploreSignupDashboard, isPendingSignup } from '../signup/activation';
import { Link, NavLink } from 'react-router-dom';
import { BookOpen, LifeBuoy, PanelLeftClose, PanelLeftOpen, Settings, ShieldCheck } from 'lucide-react';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { PendingSignupSidebar } from '../signup/PendingSignupSidebar';
import { canOpenSupport, openSupport } from './support';

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & { addon?: React.ReactNode; userMenuAddon?: React.ReactNode };

export function AppSidebar({ addon, userMenuAddon, ...props }: AppSidebarProps) {
  const auth = useSelector((state: any) => state.auth);
  if (isPendingSignup(auth) && !canExploreSignupDashboard(auth)) return <PendingSignupSidebar {...props} />;
  return <ActiveAppSidebar addon={addon} userMenuAddon={userMenuAddon} {...props} />;
}

function ActiveAppSidebar({ addon, userMenuAddon, ...props }: AppSidebarProps) {
	const { restoreOpen } = useSidebar();

	// Single owner of the pipeline-summary polling loop; FeatureNavigator and
	// PinnedFeatures both read the result from the store.
	usePipelineSummaryPoller();

	const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
	const [navLoaded, setNavLoaded] = useState(false);

	const auth = useSelector((state: any) => state.auth);
	const userId = auth?.user?._id;
	// Subscribers see the AI Employee status card at the top; everyone else
	// gets the "Enable AI Employee" offer tucked above the user menu.
	const hasAiProduct = hasAiEmployeeProduct(auth);
	const pendingSignup = canExploreSignupDashboard(auth);
	const { setOpenMobile } = useSidebar();

	const getStorageKey = (id: string) => `navStates_${id}`;

	const handleToggle = (title: string, isOpen: boolean) => {
		const updatedStates = { ...openStates, [title]: isOpen };
		setOpenStates(updatedStates);
		if (userId) {
			localStorage.setItem(getStorageKey(userId), JSON.stringify(updatedStates));
		}
	};

	function getSidebarStateFromCookie() {
		const match = document.cookie.match(
			new RegExp(`(^| )sidebar_state=([^;]+)`),
		);
		return match ? match[2] === 'true' : true;
	}

	useEffect(() => {
		restoreOpen(getSidebarStateFromCookie());
	}, [restoreOpen]);

	useEffect(() => {
		if (!userId) return;
		try {
			const saved = localStorage.getItem(getStorageKey(userId));
			if (saved) setOpenStates(JSON.parse(saved));
		} catch {
			// ignore corrupt data
		}
		setNavLoaded(true);
	}, [userId]);

	return (
		<SidebarStateProviders>
			<Sidebar collapsible="icon" variant="inset" {...props}>
				<SidebarHeader>
					<AccountSwitcher />
					<ReactivateAccountButton />
					<LocationContextIndicator />
					{pendingSignup && <SidebarMenu><SidebarMenuItem><SidebarMenuButton asChild tooltip="Get started"><Link to="/onboarding" onClick={() => setOpenMobile(false)}><ShieldCheck /><span>Get started</span></Link></SidebarMenuButton></SidebarMenuItem></SidebarMenu>}
					{addon}
				</SidebarHeader>
				<SidebarContent>
					{hasAiProduct && (
						<div className="px-2 mb-1.5 pt-1">
							<AiEmployeeWidget />
						</div>
					)}
					<NavWebchat
						handleToggle={handleToggle}
						openStates={openStates}
						showFilterGlow={navLoaded && !('Filters' in openStates)}
					/>
					<NavRunner
						handleToggle={handleToggle}
						openStates={openStates}
					/>
					<SidebarMenu className="px-2">
						<FeatureNavigator />
					</SidebarMenu>
					<PinnedFeatures />
					<PhoneAccountLinks showSupport={canOpenSupport(auth)} />
				</SidebarContent>
				{/* Landscape phones: the whole sidebar scrolls, so the footer (Expand,
				    account) stays pinned to the bottom instead of below the fold. */}
				<SidebarFooter className="md:max-xl:[@media(max-height:499px)]:sticky md:max-xl:[@media(max-height:499px)]:bottom-0 md:max-xl:[@media(max-height:499px)]:z-10 md:max-xl:[@media(max-height:499px)]:bg-sidebar">
					{!hasAiProduct && !pendingSignup && <AiEmployeeWidget />}
					<RailToggle />
					<NavUser user={auth.user} addon={userMenuAddon} />
				</SidebarFooter>
				<SidebarRail />
			</Sidebar>
		</SidebarStateProviders>
	);
}

/**
 * State that must outlive the swap between the phone drawer and the desktop
 * sidebar when a phone rotates: the More Features sheet, and the folder and
 * Refer us dialogs with what has been typed into them.
 */
function SidebarStateProviders({ children }: { children: React.ReactNode }) {
	return (
		<FeatureNavigatorStateProvider>
			<FolderDialogProvider>
				<ReferralDialogProvider>{children}</ReferralDialogProvider>
			</FolderDialogProvider>
		</FeatureNavigatorStateProvider>
	);
}

/**
 * Phone drawer only: Settings, Walkthroughs and Help as plain rows, where a
 * phone user looks for them, instead of inside the account menu.
 */
function PhoneAccountLinks({ showSupport }: { showSupport: boolean }) {
	const { isMobile } = useSidebar();
	if (!isMobile) return null;
	return (
		<SidebarMenu className="mx-2 mb-2 w-auto border-t border-border/40 pt-2">
			<SidebarMenuItem>
				<SidebarMenuButton asChild>
					<NavLink to="/settings" data-onboarding="user-settings">
						<Settings />
						<span>Settings</span>
					</NavLink>
				</SidebarMenuButton>
			</SidebarMenuItem>
			<SidebarMenuItem>
				<SidebarMenuButton asChild>
					<NavLink to="/walkthroughs">
						<BookOpen />
						<span>Walkthroughs</span>
					</NavLink>
				</SidebarMenuButton>
			</SidebarMenuItem>
			{showSupport && (
				<SidebarMenuItem>
					<SidebarMenuButton onClick={openSupport}>
						<LifeBuoy />
						<span>Help &amp; support</span>
					</SidebarMenuButton>
				</SidebarMenuItem>
			)}
		</SidebarMenu>
	);
}

/**
 * Medium screens only (768-1279px): a visible way to expand or collapse the
 * sidebar on touch screens, where the edge rail and Cmd+B aren't available.
 */
function RailToggle() {
	const { open, toggleSidebar, isMobile } = useSidebar();
	if (isMobile) return null;
	const label = open ? 'Collapse sidebar' : 'Expand sidebar';
	const Icon = open ? PanelLeftClose : PanelLeftOpen;
	return (
		<SidebarMenu className="hidden md:max-xl:flex">
			<SidebarMenuItem>
				<SidebarMenuButton
					onClick={toggleSidebar}
					tooltip={label}
					aria-label={label}
					aria-expanded={open}
					className="text-muted-foreground"
				>
					<Icon />
					<span>{open ? 'Collapse' : 'Expand'}</span>
				</SidebarMenuButton>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
